import { DRAW_STYLES } from '../../mapbox/mapbox.constants'
import { MAPBOX_ACCESS_TOKEN } from '../../mapbox/mapbox.constants'

// Mapbox ships as page globals rather than a bundled dependency, so it is reached the
// way the legacy map did. `typeof` guards below make a missing global a no-op rather
// than a crash — which is also what lets the renderer be exercised where the globals
// are absent.
declare const mapboxgl: any
declare const MapboxDraw: any

/** A polygon vertex as the engine stores it: `[lng, lat]`, longitude first. */
export type PolygonPoint = [number, number]

export interface PolygonMapConfig {
    /** The element the map draws into. */
    container: HTMLElement
    /** The polygon to open on, empty for a fresh draw. */
    points: PolygonPoint[]
    /** False for a read-only view: the polygon shows, but no draw tools are offered. */
    interactive: boolean
    /** Called with the polygon's points whenever the drawing changes; `[]` once cleared. */
    onChange: (points: PolygonPoint[]) => void
}

/** A running map, torn down when the drawer that owns it closes. */
export interface PolygonMapHandle {
    destroy(): void
}

/** The initial view: the whole world, so any saved polygon can be found and framed. */
const INITIAL_VIEW = { lng: -55.8, lat: 36.5, zoom: 1 }
/** Padding, in pixels, left around a rehydrated polygon when the map frames it. */
const FIT_PADDING = 20
const POLYGON_FEATURE_ID = 'active_polygon'

/** The MapboxDraw feature a stored polygon rehydrates as. */
function pointsToFeature(points: PolygonPoint[]) {
    return {
        id: POLYGON_FEATURE_ID,
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [points] },
    }
}

/** The points read back out of a MapboxDraw change event. */
function featureToPoints(event: { features: Array<{ geometry: { coordinates: PolygonPoint[][] } }> }): PolygonPoint[] {
    return event.features[0]?.geometry?.coordinates?.[0] ?? []
}

/**
 * Attaches a Mapbox map with polygon drawing to `container`, seeded with `points`.
 *
 * All of the page's Mapbox surface is confined here so the renderer above it holds only
 * the value it reads and emits — the working polygon — and can run without a map at all.
 * When the globals are absent (as under test) this attaches nothing and returns `null`,
 * and the drawer simply shows no map; the value the renderer already holds is untouched.
 *
 * Reads the polygon out of `draw.create`/`draw.update`/`draw.delete` and hands it up as
 * bare `[lng, lat]` points — the one shape the engine stores — so the renderer never sees
 * a GeoJSON feature. There is no network call; the map is a drawing surface only.
 */
export function attachPolygonMap(config: PolygonMapConfig): PolygonMapHandle | null {
    if (typeof mapboxgl === 'undefined' || typeof MapboxDraw === 'undefined') return null

    const map = new mapboxgl.Map({
        container: config.container,
        style: 'mapbox://styles/pushly/ck5gyftn80dmj1imxucglqhm3',
        center: [INITIAL_VIEW.lng, INITIAL_VIEW.lat],
        zoom: INITIAL_VIEW.zoom,
        accessToken: MAPBOX_ACCESS_TOKEN,
    })

    const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: config.interactive ? { polygon: true, trash: true } : {},
        styles: DRAW_STYLES,
    })
    map.addControl(draw)
    map.addControl(new mapboxgl.NavigationControl())

    const emit = (event: any) => config.onChange(featureToPoints(event))

    map.on('load', () => {
        // A map sizes its canvas from the container as it stood when the tiles loaded, so a
        // container that grew in between leaves the canvas in a strip of its own box. This covers
        // it, and the attach timing covers the rest: construction runs from the drawer's
        // `afterOpenChange`, by which point the container is already at its final size. There is
        // nothing left for an owner to call afterwards, which is why the handle offers nothing.
        map.resize()

        if (config.points.length) {
            draw.add(pointsToFeature(config.points))
            const bounds = config.points.reduce(
                (acc: any, point: PolygonPoint) => acc.extend(point),
                new mapboxgl.LngLatBounds(config.points[0], config.points[0]),
            )
            map.fitBounds(bounds, { padding: FIT_PADDING })
        }
    })

    if (config.interactive) {
        map.on('draw.create', emit)
        map.on('draw.update', emit)
        map.on('draw.delete', () => config.onChange([]))
    }

    return {
        destroy() {
            map.remove()
        },
    }
}
